import type { IdealItemDto } from '@bk/contracts';
import type { ReactNode } from 'react';
import { useTenantQuery } from '../../../lib/useTenant';

/**
 * La scheda del ruolo con gli standard della libreria (ADR 0131). Nel riquadro, sotto il nome che usa l'azienda, in
 * piccolo la professione standard e il livello; toccando il ruolo, la scheda: compiti dell'indagine INAPP (le mansioni),
 * conoscenze e abilità (contratto, fase 8), formazione,
 * abilitazioni, fasi, impianti e il costo medio del livello dalle tabelle del Ministero del Lavoro.
 */

/** La riga piccola del riquadro: la professione standard e il livello del contratto. */
export function standardLine(v: IdealItemDto): string | null {
  const parts = [
    v.professione?.nome ?? v.mestiere?.nome,
    v.contratto?.livello ? `livello ${v.contratto.livello}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

interface ProfessionCard {
  scheda: {
    tasks: Array<{ text: string; importance: number | null; frequency: number | null }>;
    eqf?: { level: number | null; note: string } | null;
    knowledge?: Array<{ label: string; importance: number | null }>;
    skills?: Array<{ label: string; importance: number | null }>;
    knowledgeNeeds?: Array<{ label: string; share: number | null }>;
    skillNeeds?: Array<{ label: string; share: number | null }>;
    related?: Array<{ code: string; correlation: number | null }>;
    similar?: Array<{ code: string }>;
  };
}
interface TrainingBodies {
  enti: Array<{ codice: string; nome: string; comune: string | null; provincia: string | null; telefoni: string[]; email: string[]; tipologie: string[] }>;
  totale: number;
  fonte: string | null;
}
interface CostRow {
  level: string;
  ccnlLevel: string | null;
  annual: number | null;
  annualComputed: boolean;
}
interface ContractCard {
  costo_lavoro: {
    tabelle: Array<{
      area: string;
      category: string | null;
      month: string;
      decree: { act: string };
      rows: CostRow[];
    }>;
  } | null;
}

const RISK: Record<string, string> = {
  basso: 'rischio basso',
  medio: 'rischio medio',
  alto: 'rischio alto',
};
const eur = (n: number) => `${Math.round(n).toLocaleString('it-IT')} €`;
const levelKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/^\s*(livello|liv\.?)\s*/, '')
    .replace(/[°ºª]/g, '')
    .replace(/\s+/g, '');

/** Gli altri ruoli dell'organigramma, con la professione: per dire quali sono vicini (ADR 0133). */
export interface OtherRole {
  nome: string;
  codice: string | null;
}

export function RoleStandards({
  v,
  others = [],
  regione = null,
  provincia = null,
}: {
  v: IdealItemDto;
  others?: OtherRole[];
  /** Dove lavora il ruolo: per gli enti di formazione accreditati della regione (ADR 0133). */
  regione?: string | null;
  provincia?: string | null;
}) {
  const cp = v.professione?.codice ?? null;
  const ccnl = v.contratto?.codice ?? null;
  const profession = useTenantQuery<ProfessionCard>(
    ['standards', 'cp2021', cp],
    `/standards/cp2021/${encodeURIComponent(cp ?? '')}`,
    { enabled: !!cp },
  );
  const contract = useTenantQuery<ContractCard>(
    ['standards', 'ccnl', ccnl],
    `/standards/ccnl/${encodeURIComponent(ccnl ?? '')}`,
    { enabled: !!ccnl && !!v.contratto?.livello },
  );
  const bodies = useTenantQuery<TrainingBodies>(
    ['standards', 'enti-formazione', regione, provincia],
    `/standards/enti-formazione?regione=${encodeURIComponent(regione ?? '')}&provincia=${encodeURIComponent(provincia ?? '')}`,
    { enabled: !!regione && !!v.formazione?.length },
  );
  const card = profession.data?.scheda;
  // Ciò che i lavoratori della professione dicono di dover aggiornare (INAPP), le quote più alte.
  const needs = [...(card?.knowledgeNeeds ?? []), ...(card?.skillNeeds ?? [])]
    .sort((a, b) => (b.share ?? 0) - (a.share ?? 0))
    .slice(0, 5);
  // I ruoli vicini (ADR 0133): stessa professione, o professione correlata (grado 1 o 0,5) o affine per INAPP.
  const close = new Set([...(card?.related ?? []).filter((r) => (r.correlation ?? 0) > 0).map((r) => r.code), ...(card?.similar ?? []).map((r) => r.code)]);
  const near = cp
    ? [...new Set(others.filter((o) => o.nome !== v.nome && o.codice && (o.codice === cp || close.has(o.codice))).map((o) => o.nome))].slice(0, 8)
    : [];
  // Conoscenze e abilità della professione (INAPP, contratto fase 8), le più importanti.
  const top = (xs?: Array<{ label: string; importance: number | null }>) => [...(xs ?? [])].sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0)).slice(0, 6);
  const knowledge = top(card?.knowledge);
  const skills = top(card?.skills);
  const tasks = [...(profession.data?.scheda.tasks ?? [])]
    .sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0))
    .slice(0, 6);
  // Il costo del livello: nella tabella nazionale se c'è, con il livello del contratto collegato o scritto allo stesso modo.
  const want = v.contratto?.livello ? levelKey(v.contratto.livello) : null;
  const cost = want
    ? [...(contract.data?.costo_lavoro?.tabelle ?? [])]
        .sort((a, b) => Number(b.area === 'nazionale') - Number(a.area === 'nazionale'))
        .flatMap((t) =>
          t.rows
            .filter((r) => levelKey(r.ccnlLevel ?? r.level) === want && r.annual != null)
            .map((r) => ({ t, r })),
        )[0]
    : undefined;
  const any =
    v.professione ||
    v.mestiere ||
    v.contratto ||
    v.rischio ||
    v.formazione?.length ||
    v.abilitazioni?.length ||
    v.impianti?.length ||
    v.verifica?.length;
  if (!any) return null;
  return (
    <div className="mt-3 rounded-xl bg-[#f7f8fb] px-3 py-3 ring-1 ring-black/5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">
        Gli standard del ruolo
      </p>
      <div className="mt-2 grid gap-x-5 gap-y-3 sm:grid-cols-2">
        {v.professione && (
          <Fact k="Professione (ISTAT CP2021)">
            {v.professione.nome} <Code>{v.professione.codice}</Code>
          </Fact>
        )}
        {v.mestiere && (
          <Fact k="Mestiere (ESCO)">
            {v.mestiere.nome} <Code>{v.mestiere.codice}</Code>
          </Fact>
        )}
        {v.contratto && (
          <Fact k="Contratto e livello">
            {v.contratto.nome} <Code>{v.contratto.codice}</Code>
            {v.contratto.livello && <span className="block">Livello {v.contratto.livello}</span>}
            {cost && (
              <span className="block text-xs text-ink/60">
                Costo medio {eur(cost.r.annual!)} l’anno
                {cost.r.annualComputed ? ' (calcolato)' : ''} · tabella del Ministero del Lavoro,{' '}
                {cost.t.decree.act}
                {cost.t.area !== 'nazionale' ? `, ${cost.t.area}` : ''}
              </span>
            )}
          </Fact>
        )}
        {card?.eqf && (
          <Fact k="Livello di qualificazione (EQF)">
            {card.eqf.level ? `EQF ${card.eqf.level}` : 'Non assegnato da INAPP'}
            <span className="block text-xs text-ink/50">numero guida INAPP per la professione</span>
          </Fact>
        )}
        {v.rischio && <Fact k="Classe di rischio (Accordo Stato-Regioni)">{RISK[v.rischio]}</Fact>}
        {!!v.formazione?.length && (
          <Fact k="Formazione">
            <ul className="list-disc pl-4">
              {v.formazione.map((f) => (
                <li key={f.corso}>
                  {f.corso}
                  {f.ore != null && ` · ${f.ore} ore`}
                  {f.norma && <span className="text-ink/50"> · {f.norma}</span>}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {!!v.abilitazioni?.length && (
          <Fact k="Abilitazioni">
            <ul className="list-disc pl-4">
              {v.abilitazioni.map((a) => (
                <li key={a.nome}>
                  {a.nome}
                  {a.norma && <span className="text-ink/50"> · {a.norma}</span>}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {!!v.impianti?.length && <Fact k="Impianti e attrezzature">{v.impianti.join(' · ')}</Fact>}
        {tasks.length > 0 && (
          <Fact k="Compiti della professione (indagine INAPP-ISTAT)">
            <ul className="list-disc pl-4">
              {tasks.map((t) => (
                <li key={t.text}>{t.text}</li>
              ))}
            </ul>
          </Fact>
        )}
        {knowledge.length > 0 && (
          <Fact k="Cosa deve sapere (conoscenze INAPP)">
            <ul className="list-disc pl-4">
              {knowledge.map((d) => (
                <li key={d.label}>
                  {d.label.trim()}
                  {d.importance != null && <span className="text-ink/50"> · importanza {Math.round(d.importance)}/100</span>}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {skills.length > 0 && (
          <Fact k="Cosa deve saper fare (abilità INAPP)">
            <ul className="list-disc pl-4">
              {skills.map((d) => (
                <li key={d.label}>
                  {d.label.trim()}
                  {d.importance != null && <span className="text-ink/50"> · importanza {Math.round(d.importance)}/100</span>}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {needs.length > 0 && (
          <Fact k="Da aggiornare (lavoratori della professione, INAPP)">
            <ul className="list-disc pl-4">
              {needs.map((n) => (
                <li key={n.label}>
                  {n.label.trim()}
                  {n.share != null && <span className="text-ink/50"> · {n.share.toLocaleString('it-IT')}%</span>}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {near.length > 0 && (
          <Fact k="Ruoli vicini, per sostituirsi o crescere">
            {near.join(' · ')}
            <span className="block text-xs text-ink/50">stessa professione o professioni correlate per INAPP</span>
          </Fact>
        )}
        {!!bodies.data?.enti.length && (
          <Fact k={`Dove fare i corsi: enti accreditati in ${regione}`}>
            <ul className="list-disc pl-4">
              {bodies.data.enti.slice(0, 4).map((e) => (
                <li key={e.codice}>
                  {e.nome}
                  {e.comune && <span className="text-ink/50"> · {e.comune}</span>}
                  {(e.telefoni[0] || e.email[0]) && <span className="block text-xs text-ink/50">{[e.telefoni[0], e.email[0]].filter(Boolean).join(' · ')}</span>}
                </li>
              ))}
            </ul>
            <span className="block text-xs text-ink/50">
              {bodies.data.totale} enti nella regione · {bodies.data.fonte}
            </span>
          </Fact>
        )}
        {!!v.verifica?.length && (
          <Fact k="Da verificare">
            <ul className="list-disc pl-4 text-amber-900">
              {v.verifica.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Fact>
        )}
      </div>
    </div>
  );
}

function Code({ children }: { children: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded bg-black/5 px-1 text-[11px] text-ink/55">
      {children}
    </span>
  );
}

function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-ink/40">{k}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}
